/**
 * NotificationsPanel — Settings → per-category × channel preferences (PR4).
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchNotificationPreferences,
  saveNotificationPreferences,
  fetchVapidPublicKey,
  subscribePush,
  type NotificationPreferences,
} from '../../../api/notifications'
import { loadSettings, saveSettings, type AriaPreferences } from '../../../api/settings'
import { sendAriaBrief, fetchAriaBriefConfig } from '../../../api/ariaBrief'

type TierKey = 'action_required' | 'money' | 'deadline' | 'fyi'

const DEFAULT_ARIA_PREFS: AriaPreferences = {
  action_mode: 'suggest',
  confirmation_threshold: 10000,
  soren_enabled: false,
  brief_enabled: true,
  brief_days: ['tuesday', 'friday'],
  voice_prefs: { voice_on_default: false, read_aloud: true },
}

const TIERS: { key: TierKey; label: string; hint: string }[] = [
  { key: 'action_required', label: 'Needs you', hint: 'Uploads waiting on you, sync problems, items to approve' },
  { key: 'money', label: 'Money', hint: 'Payments received and pay apps marked paid' },
  { key: 'deadline', label: 'Deadlines', hint: 'Due dates and lien windows coming up' },
  { key: 'fyi', label: 'Updates', hint: 'Grouped news — does not add to your Soren badge' },
]

interface Props {
  panelRef?: (el: HTMLElement | null) => void
}

export default function NotificationsPanel({ panelRef }: Props) {
  const qc = useQueryClient()
  const [pushStatus, setPushStatus] = useState<string | null>(null)

  const { data: prefs, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
  })

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: loadSettings,
  })

  const { data: briefConfig } = useQuery({
    queryKey: ['aria-brief-config'],
    queryFn: async () => (await fetchAriaBriefConfig()).data,
    retry: false,
  })

  const [briefBusy, setBriefBusy] = useState(false)
  const [briefMsg, setBriefMsg] = useState<string | null>(null)
  const [briefSendTo, setBriefSendTo] = useState('')
  const [sendToInitialized, setSendToInitialized] = useState(false)

  const canOverrideRecipient = !!briefConfig?.canOverrideRecipient

  useEffect(() => {
    if (sendToInitialized || !canOverrideRecipient) return
    const first = briefConfig?.allowlist?.[0]
    if (first) {
      setBriefSendTo(first)
      setSendToInitialized(true)
    }
  }, [briefConfig, canOverrideRecipient, sendToInitialized])

  const ariaPrefs = settings?.aria_preferences ?? DEFAULT_ARIA_PREFS
  const briefEnabled = ariaPrefs?.brief_enabled !== false
  const briefDays = ariaPrefs?.brief_days ?? ['tuesday', 'friday']

  async function toggleBrief(on: boolean) {
    await saveSettings({ aria_preferences: { ...ariaPrefs, brief_enabled: on, brief_days: briefDays } })
    qc.invalidateQueries({ queryKey: ['settings'] })
  }

  async function toggleBriefDay(day: 'tuesday' | 'friday', on: boolean) {
    const next = on ? [...new Set([...briefDays, day])] : briefDays.filter(d => d !== day)
    await saveSettings({ aria_preferences: { ...ariaPrefs, brief_enabled: briefEnabled, brief_days: next } })
    qc.invalidateQueries({ queryKey: ['settings'] })
  }

  async function handleSendBrief() {
    setBriefMsg(null)
    if (canOverrideRecipient && !briefSendTo.trim()) {
      setBriefMsg('Enter a recipient.')
      return
    }
    setBriefBusy(true)
    try {
      const res = await sendAriaBrief(
        'tuesday',
        canOverrideRecipient && briefSendTo.trim() ? { to: briefSendTo.trim() } : undefined,
      )
      const data = res.data
      if (data?.warning) {
        setBriefMsg('Sent — inbox item failed')
      } else if (data?.recipient) {
        setBriefMsg(`Sent to ${data.recipient}`)
      } else {
        setBriefMsg(res.message || 'Sent')
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Send failed'
      if (msg.toLowerCase().includes('allowlist')) {
        setBriefMsg("That address isn't on the staging allowlist.")
      } else {
        setBriefMsg(msg)
      }
    } finally {
      setBriefBusy(false)
    }
  }

  const saveMut = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) => saveNotificationPreferences(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-preferences'] }),
  })

  async function enablePush() {
    setPushStatus(null)
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushStatus('Push not supported in this browser.')
      return
    }
    try {
      const { publicKey, enabled } = await fetchVapidPublicKey()
      if (!enabled || !publicKey) {
        setPushStatus('Push not configured on server yet.')
        return
      }
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
      await subscribePush(sub.toJSON())
      setPushStatus('Browser notifications enabled.')
    } catch (e) {
      setPushStatus(e instanceof Error ? e.message : 'Could not enable push')
    }
  }

  function updateTier(tier: TierKey, field: 'push' | 'email', value: boolean | string) {
    if (!prefs) return
    const next = {
      ...prefs,
      [tier]: { ...prefs[tier], [field]: value },
    }
    saveMut.mutate(next)
  }

  if (isLoading || !prefs) {
    return (
      <section ref={panelRef} data-panel-id="notifications" className="settings-panel card">
        <p style={{ fontSize: 13, color: 'var(--ink4)' }}>Loading notification preferences…</p>
      </section>
    )
  }

  return (
    <section ref={panelRef} data-panel-id="notifications" className="settings-panel card">
      <h2 className="settings-panel-title">Notifications</h2>
      <p className="settings-panel-desc">
        Your in-app inbox is always on. Choose which categories also send push or email.
      </p>

      {TIERS.map(({ key, label, hint }) => (
        <div key={key} className="notif-tier-block">
          <div className="notif-tier-h">{label}</div>
          <div className="notif-tier-sub">{hint}</div>
          <label className="notif-check">
            <input
              type="checkbox"
              checked={prefs[key].push}
              onChange={(e) => updateTier(key, 'push', e.target.checked)}
            />
            Push when away
          </label>
          <label className="notif-check">
            Email:
            <select
              value={prefs[key].email}
              onChange={(e) => updateTier(key, 'email', e.target.value)}
            >
              <option value="off">Off</option>
              <option value="digest">Daily digest</option>
              <option value="immediate">Immediate (critical only)</option>
            </select>
          </label>
        </div>
      ))}

      <div className="notif-tier-block" data-testid="sub-upload-email-toggle">
        <div className="notif-tier-h">Sub uploads</div>
        <div className="notif-tier-sub">In-app alerts always appear in Soren when a sub uploads a document.</div>
        <label className="notif-check">
          <input
            type="checkbox"
            checked={!!prefs.sub_upload_email}
            onChange={(e) => saveMut.mutate({ ...prefs, sub_upload_email: e.target.checked })}
          />
          Email me when a sub uploads
        </label>
      </div>

      <div className="notif-push-block">
        <button type="button" className="btn" onClick={enablePush}>
          Enable browser notifications
        </button>
        {pushStatus && <p className="notif-push-status">{pushStatus}</p>}
      </div>

      <div className="notif-tier-block" data-testid="aria-brief-settings">
        <div className="notif-tier-h">Soren Brief</div>
        <div className="notif-tier-sub">Tuesday = week ahead · Friday = wrap · 7:00 AM PT · lands in inbox (FYI)</div>
        <label className="notif-check">
          <input type="checkbox" checked={briefEnabled} onChange={(e) => toggleBrief(e.target.checked)} />
          Email me the Brief
        </label>
        <label className="notif-check">
          <input type="checkbox" checked={briefDays.includes('tuesday')} disabled={!briefEnabled}
            onChange={(e) => toggleBriefDay('tuesday', e.target.checked)} />
          Tuesday
        </label>
        <label className="notif-check">
          <input type="checkbox" checked={briefDays.includes('friday')} disabled={!briefEnabled}
            onChange={(e) => toggleBriefDay('friday', e.target.checked)} />
          Friday
        </label>
        {canOverrideRecipient && (
          <label className="notif-check" style={{ marginTop: 8 }}>
            Send test to (staging only)
            <input
              type="email"
              className="hub-search"
              style={{ marginTop: 4, width: '100%' }}
              placeholder="Allowlisted email"
              value={briefSendTo}
              onChange={(e) => setBriefSendTo(e.target.value)}
              data-testid="aria-brief-send-to"
            />
          </label>
        )}
        <button type="button" className="btn b-ol btn-sm" disabled={briefBusy}
          onClick={handleSendBrief} data-testid="aria-brief-send-me">
          {briefBusy ? 'Sending…' : 'Send brief to me now'}
        </button>
        {briefMsg && <p className="notif-push-status">{briefMsg}</p>}
      </div>
    </section>
  )
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i)
  return out
}
